import type { SRT_RowSelectionState } from '@/shared/components/master-table'
import type {
	FailedPendingResponse,
	HTTPValidationError,
	PricingRuleApplyErrorDetail,
	PricingRuleApplyErrorResponse,
	PricingRuleModel,
} from '@/shared/generated'
import type { AxiosError } from 'axios'

import { useMemo, useState } from 'react'
import { RiAlertFill } from '@remixicon/react'
import { useQueryClient } from '@tanstack/react-query'
import { any, indexBy, prop } from 'lodash/fp'
import { CheckIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { usePricingCampaignsPickerTable } from '@/features/select-pricing-campaigns'

import {
	getOptimizationsMap,
	getOptStatus,
	useOptimizationsQuery,
} from '@/entities/optimization'
import {
	useOptimizationGroupsQuery,
	useWithAffectedOGsHandler,
} from '@/entities/optimization-group'
import {
	getPricingCampaignEngine,
	PricingEngineType,
	usePricingCampaignsOverviewQuery,
} from '@/entities/pricing-campaign'
import { useApplyPricingRuleMutation } from '@/entities/pricing-rule'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import {
	createSRTColumnHelper,
	MasterTable,
	SRT_TableToolbar,
	useMasterReactTable,
} from '@/shared/components/master-table'
import { Button } from '@/shared/components/ui/button'
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
} from '@/shared/components/ui/empty'
import {
	Field,
	FieldContent,
	FieldDescription,
	FieldLabel,
	FieldTitle,
} from '@/shared/components/ui/field'
import {
	Item,
	ItemContent,
	ItemDescription,
	ItemMedia,
	ItemTitle,
} from '@/shared/components/ui/item'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { Switch } from '@/shared/components/ui/switch'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'

export function ApplyPricingRuleModal({
	isOpen,
	close,
	rule,
}: {
	isOpen: boolean
	close: () => void
	rule: PricingRuleModel
}) {
	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ModalContent
				className='max-w-5xl'
				data-testid='apply-pricing-rule-modal'
			>
				<ErrorBoundary>
					<ApplyPricingRuleContent rule={rule} close={close} />
				</ErrorBoundary>
			</ModalContent>
		</Modal>
	)
}

function ApplyPricingRuleContent({
	rule,
	close,
}: {
	rule: PricingRuleModel
	close: () => void
}) {
	const queryClient = useQueryClient()
	const { t } = useTranslation()

	const [rowSelection, setRowSelection] = useState<SRT_RowSelectionState>({})
	const [forceUpdate, setForceUpdate] = useState(false)

	const overviewQuery = usePricingCampaignsOverviewQuery({})
	const optimizationGroupsQuery = useOptimizationGroupsQuery()
	const optimizationsQuery = useOptimizationsQuery({
		select: getOptimizationsMap,
	})
	const baseApplyMutation = useApplyPricingRuleMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['pricing-rules'] })
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns'],
			})
			queryClient.invalidateQueries({
				queryKey: ['pricing-campaigns-overview'],
			})
			toast.success(t('pricing_rule.apply.success', { ns: 'rule-builder' }))
			close()
		},
	})
	const applyMutation = useWithAffectedOGsHandler(baseApplyMutation, {
		onCancel: () => {
			baseApplyMutation.reset()
		},
	})
	const isLoading = any(prop('isLoading'), [
		overviewQuery,
		optimizationGroupsQuery,
		optimizationsQuery,
	])
	const data = useMemo(() => {
		const ogMap = indexBy('id', optimizationGroupsQuery.data)

		return (overviewQuery.data ?? []).map((pc) => ({
			id: pc.id,
			name: pc.name,
			engine: getPricingCampaignEngine(pc),
			optimization_group_id: pc.optimization_group_id!,
			optimization_group_name: pc.optimization_group_id
				? ogMap?.[pc.optimization_group_id]?.name
				: undefined,
			optimization_status: pc.optimization_group_id
				? getOptStatus(
						optimizationsQuery.data?.[pc.optimization_group_id]?.status
					)
				: undefined,
		}))
	}, [
		overviewQuery.data,
		optimizationGroupsQuery.data,
		optimizationsQuery.data,
	])
	const options = useMemo(
		() => ({
			optimization_group_name:
				optimizationGroupsQuery.data?.map((og) => ({
					label: og.name,
					value: og.name,
				})) || [],
		}),
		[optimizationGroupsQuery.data]
	)
	const table = usePricingCampaignsPickerTable({
		isLoading: isLoading || applyMutation.isPending,
		options,
		data,
		rowSelection,
		columnVisibility: { products_count: false },
		setRowSelection,
		columnFilters: [{ id: 'engine', value: [PricingEngineType.RB] }],
		enableRowSelection: (row) =>
			!row.getIsGrouped() && row.original.engine === PricingEngineType.RB,
	})

	const submit = (force_update: boolean) =>
		applyMutation.mutate({
			id: rule.id,
			data: { pricing_campaign_ids: Object.keys(rowSelection), force_update },
		})

	const { error } = applyMutation

	if (error) {
		// ponytail: RULE_001 assumed to be the apply-failed code (spec does not name it)
		const isApplyFailed =
			error.response?.status === 400 &&
			(error.response.data as PricingRuleApplyErrorResponse).code === 'RULE_003'

		return (
			<>
				<ModalHeader>
					<ModalTitle>
						{t('pricing_rule.apply.failed', { ns: 'rule-builder' })}
					</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<div
						className='flex flex-col gap-3'
						data-testid='apply-pricing-rule-errors'
					>
						<ApplyError error={error} names={indexBy('id', data)} />
					</div>
				</ModalBody>
				<ModalFooter className='flex justify-between'>
					<Button
						data-testid='apply-pricing-rule-back'
						onClick={() => applyMutation.reset()}
					>
						{t('general_back')}
					</Button>
					{isApplyFailed && (
						<Button
							data-testid='apply-pricing-rule-force-apply'
							variant='primary'
							disabled={applyMutation.isPending}
							onClick={() => submit(true)}
						>
							{t('pricing_rule.apply.force_apply', { ns: 'rule-builder' })}
						</Button>
					)}
				</ModalFooter>
			</>
		)
	}

	return (
		<>
			<ModalHeader>
				<ModalTitle>
					{t('pricing_rule.apply.title', { ns: 'rule-builder' })}
				</ModalTitle>
			</ModalHeader>
			<ModalBody className='gap-2'>
				<div className='pb-2'>
					<SRT_TableToolbar table={table} />
				</div>
				<div className='border-base flex h-96 flex-1 flex-col overflow-hidden rounded-lg border border-solid'>
					<MasterTable table={table} />
				</div>
				<FieldLabel
					htmlFor='apply-pricing-rule-force-update'
					className='mt-4 bg-muted/30'
				>
					<Field orientation='horizontal'>
						<FieldContent>
							<FieldTitle>
								{t('pricing_rule.apply.force_update', { ns: 'rule-builder' })}
							</FieldTitle>
							<FieldDescription>
								{t('pricing_rule.apply.force_update.description', {
									defaultValue:
										'If enabled, in case of conflict, the pricing campaign will be updated forcefully. ',
									ns: 'rule-builder',
								})}
							</FieldDescription>
						</FieldContent>
						<Switch
							id='apply-pricing-rule-force-update'
							data-testid='apply-pricing-rule-force-update'
							checked={forceUpdate}
							onCheckedChange={setForceUpdate}
						/>
					</Field>
				</FieldLabel>
			</ModalBody>
			<ModalFooter className='flex items-center justify-between'>
				<div className='flex gap-2'>
					<ModalClose asChild>
						<Button data-testid='apply-pricing-rule-cancel'>
							{t('general_cancel')}
						</Button>
					</ModalClose>
					<Button
						data-testid='apply-pricing-rule-apply'
						variant='primary'
						disabled={
							Object.keys(rowSelection).length === 0 || applyMutation.isPending
						}
						onClick={() => submit(forceUpdate)}
					>
						<CheckIcon />
						{t('general_apply')}
					</Button>
				</div>
			</ModalFooter>
		</>
	)
}

function ApplyError({
	error,
	names,
}: {
	error: AxiosError<
		PricingRuleApplyErrorResponse | HTTPValidationError | FailedPendingResponse
	>
	names: Record<string, { name: string }>
}) {
	const { t } = useTranslation()
	const status = error.response?.status

	if (
		isPrettyError(error.response?.data) &&
		error.response?.data.code === 'RULE_003'
	) {
		const body = error.response?.data as PricingRuleApplyErrorResponse

		return (
			<div className='flex h-96 flex-col gap-2 overflow-hidden'>
				<Item size='xs' variant='muted'>
					<ItemMedia variant='icon'>
						<RiAlertFill className='text-yellow-500' />
					</ItemMedia>
					<ItemContent>
						<ItemTitle>
							{t(`pr.error.${body.message}.title`, { ns: 'rule-builder' })}
						</ItemTitle>
						<ItemDescription>
							{t(`pr.error.${body.message}.description`, {
								ns: 'rule-builder',
							})}
						</ItemDescription>
					</ItemContent>
				</Item>
				<ApplyFailedTable details={body.detail} names={names} />
			</div>
		)
	}

	if (status === 422) {
		const body = error.response?.data as HTTPValidationError

		return (
			<Empty>
				<EmptyHeader>
					<EmptyMedia variant='icon'>
						<RiAlertFill className='text-yellow-500' />
					</EmptyMedia>
					<EmptyDescription>
						{t('pricing_rule.apply.failed', { ns: 'rule-builder' })}
					</EmptyDescription>
				</EmptyHeader>
				<EmptyContent>
					<div className='flex max-h-56 flex-col gap-2 overflow-y-auto rounded-md border bg-muted p-2 text-left font-mono text-xs'>
						{body.detail?.map((d) => (
							<div
								key={`${d.loc.join('.')}-${d.msg}`}
								className='flex gap-2 outline-none focus-visible:ring-1 focus-visible:ring-white/30'
							>
								<span
									aria-hidden
									className='inline-block w-[2ch] shrink-0 font-bold'
								>
									◆
								</span>
								<div className='min-w-0'>
									<div>{d.loc.join('.')}</div>
									<p className='mt-0.5 max-w-prose pl-0 text-[12px] text-muted-foreground'>
										{d.msg}
									</p>
								</div>
							</div>
						))}
					</div>
				</EmptyContent>
			</Empty>
		)
	}

	return (
		<Empty>
			<EmptyHeader>
				<EmptyMedia variant='icon'>
					<RiAlertFill className='text-yellow-500' />
				</EmptyMedia>
				<EmptyDescription>
					{t('error_occured', { defaultValue: 'Something went wrong' })}
				</EmptyDescription>
			</EmptyHeader>
			<EmptyContent className='text-sm'>{error.message}</EmptyContent>
		</Empty>
	)
}

type FailedRow = PricingRuleApplyErrorDetail & { campaign: string }
const failedColumnHelper = createSRTColumnHelper<FailedRow>()

function ApplyFailedTable({
	details,
	names,
}: {
	details: PricingRuleApplyErrorDetail[]
	names: Record<string, { name: string }>
}) {
	const { t } = useTranslation()
	const data = useMemo(
		() =>
			details.map((d) => ({
				...d,
				campaign: names[d.pricing_campaign_id]?.name ?? d.pricing_campaign_id,
			})),
		[details, names]
	)
	const columns = useMemo(
		() => [
			failedColumnHelper.accessor('campaign', {
				header: t('pricing_campaign'),
				Cell: ({ cellValue }) => <div className='text-sm'>{cellValue}</div>,
				size: 300,
			}),
			failedColumnHelper.accessor('error_type', {
				header: t('pricing_rule.apply.error_type', {
					defaultValue: 'Error type',
				}),
				Cell: ({ cellValue }) => (
					<Tooltip>
						<TooltipTrigger>
							<div className='cursor-help text-sm text-destructive'>
								{t(`pricing_rule.apply.error_type.${cellValue}`, {
									ns: 'rule-builder',
								})}
							</div>
						</TooltipTrigger>
						<TooltipContent>
							{t(`pricing_rule.apply.error_type.${cellValue}.description`, {
								ns: 'rule-builder',
							})}
						</TooltipContent>
					</Tooltip>
				),
				size: 200,
			}),
			failedColumnHelper.accessor('parameter_name', {
				header: t('pricing_rule.apply.parameter_name', {
					defaultValue: 'Parameter',
				}),
				Cell: ({ cellValue }) => (
					<div className='font-mono text-sm'>{cellValue}</div>
				),
			}),
		],
		[t]
	)
	const table = useMasterReactTable<FailedRow>({
		data,
		columns,
		layoutMode: 'grid',
		enableVirtualization: true,
		enableColumnActions: false,
		enableColumnPinning: false,
		enableRowSelection: false,
		enableSorting: false,
		defaultRowHeight: 36,
		defaultColumn: {
			minSize: 30,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		},
	})

	return (
		<div
			className='border-base flex flex-1 flex-col overflow-hidden rounded-lg border border-solid'
			data-testid='apply-pricing-rule-errors-table'
		>
			<MasterTable table={table} />
		</div>
	)
}
type PrettyError = {
	code: string
	message: string
	detail: any
}
const isPrettyError = (error: any): error is PrettyError => 'code' in error
